import React, { useEffect, useState } from "react";
import { useLocation } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { userRequest } from '../../../utils/requestMethods';
import { AlertNotification, BackButton, CustomPagination, SkeletonTable } from '../../../components';
import { useStateContext } from '../../../contexts/ContextProvider';
import { FaCheck } from 'react-icons/fa';
import { ImSpinner } from "react-icons/im";

const UploadStudentResult = ({ history }) => {
    const location = useLocation();
    const studentId = location.pathname.split('/')[4];
    const student = location.state?.student;
    console.log(student);
    const classId = student.class_info._id;
    const { user, school } = useStateContext();

    const [subjects, setSubjects] = useState([]);
    const [scores, setScores] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingScore, setSavingScore] = useState({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [results, setResults] = useState([]);

    useEffect(() => {
        const fetchSubjects = async () => {
            if (!school?._id || !user?._id || !classId) return;
            setLoading(true);
            try {
                const { data } = await userRequest.get(
                    `/teachers/teacher-assigned-subjects/${school._id}/${user._id}?limit=100`,
                );
                const assignedForThisArm = (data.subjects || []).filter(
                    (subject) => String(subject.class?._id || subject.class) === String(classId),
                );
                setSubjects(assignedForThisArm);
            } catch (error) {
                console.error("Error fetching subjects:", error);
                setSubjects([]);
            } finally {
                setLoading(false);
            }
        };
        fetchSubjects();
    }, [school?._id, user?._id, classId]);

    const fetchResult = async () => {
        //setLoading(true);
        try {
            const { data } = await userRequest.get(`result/${school?._id}/${studentId}?term=${school?.settings?.current_term}&session=${school?.settings?.current_session}`);
            setResults(data.results || []);
        } catch (error) {
            console.error("Error fetching results:", error);
        } finally {
            //setLoading(false);
        }
    };

    useEffect(() => {
        fetchResult();
    }, [school?._id, user?._id]);

    // Initialize scores from existing results
    useEffect(() => {
        if (results.length > 0 && school?.settings?.assessment?.ca?.cas) {
            const initialScores = {};
            
            results.forEach(result => {
                const subjectId = result.subject.id;
                initialScores[subjectId] = initialScores[subjectId] || {};

                // Exam score
                initialScores[subjectId].exam = result.exam?.toString() || '';

                // CA scores
                result.cas.forEach(caEntry => {
                    const caIndex = caEntry.ca - 1;
                    const caId = school.settings.assessment.ca.cas[caIndex]?._id;
                    if (caId) {
                        initialScores[subjectId][caId] = caEntry.score?.toString() || '';
                    }
                });
            });

            setScores(prev => ({ ...prev, ...initialScores }));
        }
    }, [results, school?.settings?.assessment?.ca?.cas]);

    const handleScoreChange = (subjectId, assessmentId, value) => {
        setScores(prev => ({
            ...prev,
            [subjectId]: {
                ...prev[subjectId],
                [assessmentId]: value,
            },
        }));
    };

    const saveScore = async (subjectId, assessmentId) => {
        if (!scores[subjectId]?.[assessmentId]) return;
    
        const isExam = assessmentId === 'exam';
        const basePayload = {
            childId: studentId,
            schoolId: school?._id,
            session: school?.settings?.current_session,
            term: school?.settings?.current_term,
            classId,
            subjectId,
        };
    
        try {
            setSavingScore(prev => ({ ...prev, [`${subjectId}-${assessmentId}`]: true }));
            setError('');
            setSuccess(false);
    
            if (isExam) {
                const scoreValue = parseInt(scores[subjectId][assessmentId]) || 0;
                const maxScore = school?.settings?.assessment?.exam?.max_score;
                if (scoreValue > maxScore) {
                    throw new Error(`Exam score cannot exceed ${maxScore}`);
                }
    
                const subjectResult = results.find(r => r.subject.id === subjectId);
                const endpoint = '/result/exam';
                
                if (subjectResult?.exam !== null) {
                    // Update existing exam score
                    await userRequest.put(endpoint, {
                        ...basePayload,
                        newScore: scoreValue
                    });
                } else {
                    // Create new exam score
                    await userRequest.post(endpoint, {
                        ...basePayload,
                        score: scoreValue
                    });
                }
            } else {
                const caIndex = school?.settings?.assessment?.ca?.cas.findIndex(ca => ca._id === assessmentId);
                if (caIndex === -1) return;
                
                const scoreValue = parseInt(scores[subjectId][assessmentId]) || 0;
                const maxScore = school?.settings?.assessment?.ca?.cas[caIndex]?.max_score;
                if (scoreValue > maxScore) {
                    throw new Error(`Score for CA ${caIndex + 1} cannot exceed ${maxScore}`);
                }
    
                const caNumber = caIndex + 1;
                const subjectResult = results.find(r => r.subject.id === subjectId);
                const existingCA = subjectResult?.cas?.find(ca => ca.ca === caNumber);
                const endpoint = '/result/ca';
    
                if (existingCA) {
                    // Update existing CA score
                    await userRequest.put(endpoint, {
                        ...basePayload,
                        results: [{
                            ca: caNumber,
                            newScore: scoreValue
                        }]
                    });
                } else {
                    // Create new CA score
                    await userRequest.post(endpoint, {
                        ...basePayload,
                        results: [{
                            ca: caNumber,
                            score: scoreValue
                        }]
                    });
                }
            }
    
            setSuccess(true);
            setTimeout(() => setSuccess(false), 2000);
            await fetchResult(); // Refresh results after save
        } catch (error) {
            console.error(error);
            setError(error.response?.data || error.message);
            setTimeout(() => setError(''), 5000);
        } finally {
            setSavingScore(prev => ({ ...prev, [`${subjectId}-${assessmentId}`]: false }));
        }
    };

    const caColumns = school?.settings?.assessment?.ca?.cas?.map((ca, index) => ({
        name: `CA ${index + 1} (${ca.max_score})`,
        selector: row => scores[row._id]?.[ca._id] || '',
        cell: (row) => (
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    min="0"
                    max={ca.max_score}
                    value={scores[row._id]?.[ca._id] || ''}
                    onChange={(e) => handleScoreChange(row._id, ca._id, e.target.value)}
                    className="border p-1 w-16 rounded"
                />
                <button 
                    onClick={() => saveScore(row._id, ca._id)} 
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm p-2.5 text-center inline-flex items-center me-2"
                >
                    {savingScore[`${row._id}-${ca._id}`] ? 
                        <ImSpinner className="animate-spin text-gray-400" /> : 
                        <FaCheck />
                    }
                </button>
            </div>
        ),
    }));

    const examColumn = {
        name: `Exam (${school?.settings?.assessment?.exam?.max_score})`,
        cell: (row) => (
            <div className="flex items-center gap-2">
                <input
                    type="number"
                    min="0"
                    max={school?.settings?.assessment?.exam?.max_score}
                    value={scores[row._id]?.exam || ''}
                    onChange={(e) => handleScoreChange(row._id, 'exam', e.target.value)}
                    className="border p-1 w-16 rounded"
                />
                <button 
                    onClick={() => saveScore(row._id, 'exam')} 
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm p-2.5 text-center inline-flex items-center me-2"
                >
                    {savingScore[`${row._id}-exam`] ? 
                        <ImSpinner className="animate-spin text-gray-400" /> : 
                        <FaCheck />
                    }
                </button>
            </div>
        ),
    };

    const columns = [
        { name: "Subject Name", selector: row => row.subject_name },
        ...caColumns,
        examColumn,
    ];

    return (
        <div className="w-full flex items-center justify-center py-14 md:py-8 relative">
            <div className="bg-white rounded-2xl p-6 w-full mx-3 md:mx-9">
                <BackButton history={history} />
                <h1 className="text-lg font-semibold mb-4">Upload {student.lastname} {student.firstname} Result</h1>

                {error && <p className="text-red-600">{error}</p>}
                {success && <p className="text-green-600">Results submitted successfully!</p>}

                <div className="border rounded-3xl">
                    <DataTable
                        data={subjects}
                        columns={columns}
                        pagination
                        paginationComponent={CustomPagination}
                        customStyles={{ headRow: { style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: "bold" } } }}
                        progressPending={loading}
                        progressComponent={<SkeletonTable rows={5} columns={6} />}
                        highlightOnHover
                        striped
                        noDataComponent={
                            <div className="py-10 text-center">
                                <p className="text-sm font-medium text-slate-700">No subjects assigned to you for this class.</p>
                                <p className="mt-1 text-xs text-slate-500">Ask an admin to assign you to a subject before entering results.</p>
                            </div>
                        }
                    />
                </div>
            </div>
            {error && 
                <AlertNotification type="error" message={error} onClose={() => setError("")} />}
            {success && 
                <AlertNotification type="success" message="Results submitted successfully!" onClose={() => setSuccess("")} />}
        </div>
    );
};

export default UploadStudentResult;
